/**
 * Attendance years: enrollments (2024+) + 2022/2023 pasted roster.
 * Showcase/competition never decide whether someone came.
 */

import { IABF_ADMIN_YEARS, dancersCollection } from '@/config/iabf-event'
import { normalizeEmail } from '@/utils/iabf-people'
import earlyRosters from '@/data/iabf-early-year-rosters.json'

function unionYears (lists) {
  const set = new Set()
  for (const list of lists) {
    for (const raw of list || []) {
      const n = Number(raw)
      if (Number.isFinite(n) && n > 0) {
        set.add(n)
      }
    }
  }
  return Array.from(set).sort((a, b) => a - b)
}

const EARLY_YEARS_BY_EMAIL = (() => {
  const map = new Map()
  Object.keys(earlyRosters || {}).forEach((yearStr) => {
    const year = Number(yearStr)
    const rows = earlyRosters[yearStr] || []
    rows.forEach((row) => {
      const email = normalizeEmail(row && row.email)
      if (!email) {
        return
      }
      if (!map.has(email)) {
        map.set(email, new Set())
      }
      map.get(email).add(year)
    })
  })
  return map
})()

/** 2022/2023 roster years for this email (and aliases). */
export function earlyRosterYearsForEmails (emails) {
  const set = new Set()
  for (const raw of emails || []) {
    const email = normalizeEmail(raw)
    const years = email ? EARLY_YEARS_BY_EMAIL.get(email) : null
    if (!years) {
      continue
    }
    years.forEach((y) => set.add(y))
  }
  return Array.from(set).sort((a, b) => a - b)
}

export function enrollmentYearsToScan () {
  return IABF_ADMIN_YEARS.filter((y) => !!dancersCollection(y))
}

/**
 * Final yearsAttended list. Canceled years are dropped even if they appear
 * on a roster or leftover person field.
 */
export function computeAttendanceYears ({
  enrollmentYears = [],
  earlyRosterYears = [],
  liveYear,
  canceledYears = []
} = {}) {
  const canceled = new Set(
    (canceledYears || []).map(Number).filter((n) => Number.isFinite(n) && n > 0)
  )
  const live = Number(liveYear)
  const withLive = Number.isFinite(live) && live > 0 && !canceled.has(live)
    ? [live]
    : []
  return unionYears([enrollmentYears, earlyRosterYears, withLive])
    .filter((y) => !canceled.has(y))
}

/** Coerce stored years to numbers. Never iterate a "2026, 2027" string as chars. */
export function coerceAttendanceYearList (raw) {
  if (Array.isArray(raw)) {
    return unionYears([raw])
  }
  if (typeof raw === 'number') {
    return unionYears([[raw]])
  }
  if (typeof raw === 'string' && raw.trim()) {
    return unionYears([raw.split(/[\s,]+/)])
  }
  return []
}

/**
 * Years for the dancer on screen. Ignores a leftover person record
 * (admin masquerade) unless personId matches.
 */
export function yearsAttendedForDancer (person, dancer, liveYear) {
  const p = person || {}
  const d = dancer || {}
  const dancerPid = d.personId || null
  const personOk = !!(p.id && (!dancerPid || p.id === dancerPid))
  let raw = []
  if (personOk && p.yearsAttended != null) {
    raw = p.yearsAttended
  } else if (d.yearsAttended != null) {
    raw = d.yearsAttended
  }
  const years = coerceAttendanceYearList(raw)
  const live = Number(liveYear != null ? liveYear : d.festivalYear)
  return computeAttendanceYears({
    enrollmentYears: years,
    liveYear: live,
    canceledYears: d.canceledYears
  })
}
